import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import AdminLogin from "./AdminLogin";
import ReservationsAdmin from "./ReservationsAdmin";
import Dashboard from "./Dashboard";
import ToursAdmin from "./ToursAdmin";
import PricesAdmin from "./PricesAdmin";
import GalleryAdmin from "./GalleryAdmin";
import ContentAdmin from "./ContentAdmin";
import LocationsAdmin from "./LocationsAdmin";
import { ContactAdmin, AvailabilityAdmin, ConfigAdmin } from "./SettingsAdmin";
import CustomersAdmin from "./CustomersAdmin";
import PaymentsAdmin from "./PaymentsAdmin";
import OperationsAdmin from "./OperationsAdmin";
import BusinessAdmin from "./BusinessAdmin";
import ReportsAdmin from "./ReportsAdmin";
import MediaAdmin from "./MediaAdmin";
import { rpc } from "@/lib/booking";

const TABS = [
    ["dashboard", "Dashboard"],
    ["reservas", "Reservas"],
    ["clientes", "Clientes"],
    ["pagamentos", "Pagamentos"],
    ["operacao", "Operação"],
    ["crescimento", "Crescimento"],
    ["relatorios", "Relatórios"],
    ["midias", "Galeria privada"],
    ["passeios", "Passeios"],
    ["precos", "Preços"],
    ["fotos", "Fotos"],
    ["conteudo", "Conteúdo"],
    ["locais", "Locais"],
    ["contato", "WhatsApp / Contato"],
    ["disponibilidade", "Disponibilidade"],
    ["config", "Configurações"],
];

export default function AdminApp() {
    const [status, setStatus] = useState(
        isSupabaseConfigured ? "loading" : "anon",
    );
    const [warn, setWarn] = useState("");
    const [tab, setTab] = useState("dashboard");
    const [role, setRole] = useState(null);

    useEffect(() => {
        if (status !== "ok") return;
        rpc("admin_role", {}).then((r) => { if (["owner","manager","staff"].includes(r)) setRole(r); else {setWarn("Perfil administrativo não confirmado."); setStatus("warn");} }).catch(() => { setWarn("Falha ao verificar permissões."); setStatus("warn"); });
    }, [status]);
    const tabs = TABS.filter(([id]) => role !== "staff" || !["pagamentos","crescimento","relatorios"].includes(id));

    useEffect(() => {
        if (!isSupabaseConfigured) return;
        let sub;
        supabase.auth.getSession().then(async ({ data }) => {
            const session = data.session;
            if (!session) {
                setStatus("anon");
                return;
            }
            const { data: row, error } = await supabase
                .from("admin_users")
                .select("user_id")
                .eq("user_id", session.user.id)
                .maybeSingle();
            if (error) {
                setWarn(
                    "Não foi possível verificar a permissão de administrador (tabela admin_users). Confira se o SQL de configuração foi aplicado no Supabase.",
                );
                setStatus("warn");
            } else if (!row) {
                await supabase.auth.signOut();
                setStatus("anon");
            } else {
                setStatus("ok");
            }
        });
        const { data: s } = supabase.auth.onAuthStateChange((_e, sess) => {
            if (!sess) setStatus("anon");
        });
        sub = s;
        return () => sub?.subscription?.unsubscribe();
    }, []);

    if (status === "loading") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ocean font-body text-dim">
                <p className="font-mono2 text-xs uppercase tracking-[0.3em]">
                    Carregando...
                </p>
            </div>
        );
    }
    if (status === "anon") return <Navigate to="/admin/login" replace />;

    if (status === "warn") {
        return (
            <div className="grain min-h-screen bg-ocean px-5 py-16 font-body text-ink">
                <div className="mx-auto max-w-lg rounded-3xl border border-neon-sun/40 bg-deep p-8">
                    <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neon-sun">
                        Atenção
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-dim">
                        {warn}
                    </p>
                    <div className="mt-8 flex gap-4">
                        <a
                            href="/"
                            data-testid="admin-warn-back-site"
                            className="inline-flex rounded-full border border-ink/25 px-6 py-3 font-display text-[10px] font-extrabold uppercase tracking-widest transition-colors hover:border-neon-cyan hover:text-neon-cyan"
                        >
                            Voltar ao site
                        </a>
                        <button
                            onClick={() => supabase.auth.signOut()}
                            data-testid="admin-warn-logout"
                            className="inline-flex rounded-full border border-neon-coral/40 px-6 py-3 font-display text-[10px] font-extrabold uppercase tracking-widest text-neon-coral"
                        >
                            Sair
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "ok" && !role) return <div className="min-h-screen flex items-center justify-center bg-ocean text-dim" role="status">Verificando permissões administrativas…</div>;
    const screens = {
        dashboard: <Dashboard go={setTab} />,
        reservas: <ReservationsAdmin role={role} />,
        clientes: <CustomersAdmin role={role} />,
        pagamentos: <PaymentsAdmin />,
        operacao: <OperationsAdmin />,
        crescimento: <BusinessAdmin role={role} />,
        relatorios: <ReportsAdmin />,
        midias: <MediaAdmin />,
        passeios: <ToursAdmin />,
        precos: <PricesAdmin />,
        fotos: <GalleryAdmin />,
        conteudo: <ContentAdmin />,
        locais: <LocationsAdmin />,
        contato: <ContactAdmin />,
        disponibilidade: <AvailabilityAdmin />,
        config: <ConfigAdmin role={role} />,
    };

    return (
        <div className="grain min-h-screen bg-ocean font-body text-ink">
            <header className="sticky top-0 z-40 border-b border-neon-cyan/10 bg-ocean/85 backdrop-blur-xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-8 sm:py-4">
                    <p className="min-w-0 truncate font-mono2 text-[10px] font-semibold uppercase tracking-[0.15em] text-neon-cyan sm:text-[11px] sm:tracking-[0.3em]">
                        Jetski Paradise • Admin
                    </p>
                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <a
                            href="/"
                            data-testid="admin-view-site"
                            className="inline-flex min-h-11 items-center rounded-full border border-ink/25 px-3 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-dim transition-colors hover:border-neon-cyan hover:text-neon-cyan"
                        >
                            Ver site
                        </a>
                        <button
                            onClick={() => supabase.auth.signOut()}
                            data-testid="admin-logout"
                            className="inline-flex min-h-11 items-center rounded-full border border-neon-coral/40 px-3 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-coral transition-colors hover:bg-neon-coral/10"
                        >
                            Sair
                        </button>
                    </div>
                </div>
                <div className="mx-auto max-w-7xl px-3 pb-2 sm:px-8 md:hidden">
  <label htmlFor="admin-mobile-view" className="mb-1 block font-mono2 text-[10px] uppercase tracking-widest text-dim">Área do painel</label>
  <select id="admin-mobile-view" value={tab} onChange={(e)=>setTab(e.target.value)} className="min-h-12 w-full rounded-xl border border-neon-cyan/30 bg-deep px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-neon-cyan">
  {tabs.map(([id,label])=><option value={id} key={id}>{label}</option>)}
  </select></div>
  <div className="mx-auto max-w-7xl overflow-x-auto px-3 pb-3 sm:px-8 md:hidden">
                    <div className="flex gap-2">
                        {tabs.map(([id, label]) => (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                data-testid={`admin-nav-${id}`}
                                className={`min-h-11 shrink-0 rounded-full border px-4 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                                    tab === id
                                        ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan"
                                        : "border-ink/15 text-dim"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <div className="mx-auto flex max-w-7xl gap-8 px-3 py-5 sm:px-8 sm:py-10">
                <aside className="hidden w-56 shrink-0 md:block">
                    <nav className="sticky top-28 space-y-1">
                        {tabs.map(([id, label]) => (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                data-testid={`admin-nav-${id}`}
                                className={`block w-full rounded-xl px-4 py-2.5 text-left font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                                    tab === id
                                        ? "bg-neon-cyan/10 text-neon-cyan"
                                        : "text-dim hover:text-ink"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </nav>
                </aside>
                <main className="min-w-0 flex-1" data-testid="admin-panel">
                    {screens[tab]}
                </main>
            </div>
        </div>
    );
}
